import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Buildings, FileText, Stamp, FileArrowUp, FileArrowDown,
  CheckSquare, CurrencyDollar, ArrowLeft, CaretRight,
} from "@phosphor-icons/react";

const typeConfig = {
  contract: { icon: FileText, color: "bg-[#002FA7]", label: "Contract", link: "/contracts" },
  loa: { icon: Stamp, color: "bg-[#7C3AED]", label: "LOA", link: "/loa" },
  form470: { icon: FileArrowUp, color: "bg-[#0891B2]", label: "Form 470", link: "/forms/470" },
  form471: { icon: FileArrowDown, color: "bg-[#002FA7]", label: "Form 471", link: "/forms/471" },
  form486: { icon: CheckSquare, color: "bg-[#00B341]", label: "Form 486", link: "/forms/486" },
  form472: { icon: CurrencyDollar, color: "bg-[#D97706]", label: "Form 472 BEAR", link: "/invoices" },
  form474: { icon: CurrencyDollar, color: "bg-[#D97706]", label: "Form 474 SPI", link: "/invoices" },
};

const statusBadge = (status) => {
  const colors = {
    active: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
    signed: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
    approved: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
    committed: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
    paid: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
    pending: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20",
    draft: "bg-gray-100 text-[#6B7280] border-gray-200",
    open: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
    submitted: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
    expired: "bg-gray-100 text-[#6B7280] border-gray-200",
    denied: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
    rejected: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
  };
  return colors[status] || "bg-gray-100 text-[#6B7280] border-gray-200";
};

export default function ClientTimeline() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}/timeline`)
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Failed to load timeline"); navigate("/clients"); })
      .finally(() => setLoading(false));
  }, [clientId, navigate]);

  if (loading) return <div className="p-8 flex items-center justify-center h-full"><p className="text-[#6B7280] text-sm">Loading timeline...</p></div>;
  if (!data) return null;

  const { client, events } = data;

  // Group events by year-month
  const grouped = {};
  events.forEach((e) => {
    const d = e.date || "";
    const key = d.length >= 7 ? d.substring(0, 7) : "Unknown";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(e);
  });

  const formatMonth = (key) => {
    if (key === "Unknown") return "Unknown Date";
    try {
      const [y, m] = key.split("-");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${months[parseInt(m, 10) - 1]} ${y}`;
    } catch { return key; }
  };

  return (
    <div className="p-6" data-testid="client-timeline-page">
      {/* Header */}
      <div className="mb-6">
        <button data-testid="back-to-clients" onClick={() => navigate("/clients")} className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#002FA7] mb-3 transition-colors">
          <ArrowLeft size={14} /> Back to Clients
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center flex-shrink-0">
            <Buildings size={20} className="text-white" weight="bold" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{client.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <Badge variant="outline" className="rounded-none text-xs capitalize">{client.entity_type}</Badge>
              {client.ben && <span className="text-xs font-mono text-[#6B7280]">BEN: {client.ben}</span>}
              {client.city && client.state && <span className="text-xs text-[#6B7280]">{client.city}, {client.state}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 sm:grid-cols-7 gap-0 border border-gray-200 mb-6">
        {Object.entries(typeConfig).map(([key, cfg]) => {
          const count = events.filter((e) => e.type === key).length;
          return (
            <div key={key} className="p-3 border-r border-gray-200 last:border-r-0 text-center">
              <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] block">{cfg.label}</span>
              <span className="text-lg font-bold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{count}</span>
            </div>
          );
        })}
      </div>

      {/* Timeline */}
      {events.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <Buildings size={32} className="mx-auto text-[#6B7280] mb-3" />
          <p className="text-sm text-[#6B7280]">No activity recorded for this client yet.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([month, monthEvents]) => (
            <div key={month}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] bg-[#F8F9FA] px-3 py-1">{formatMonth(month)}</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="space-y-0">
                {monthEvents.map((event, i) => {
                  const cfg = typeConfig[event.type] || typeConfig.contract;
                  const Icon = cfg.icon;
                  return (
                    <div key={`${event.id}-${i}`} className="flex gap-4" data-testid={`timeline-event-${event.id}`}>
                      {/* Timeline line */}
                      <div className="flex flex-col items-center w-8 flex-shrink-0">
                        <div className={`w-7 h-7 ${cfg.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon size={14} className="text-white" weight="bold" />
                        </div>
                        {i < monthEvents.length - 1 && <div className="w-px flex-1 bg-gray-200 min-h-[24px]" />}
                      </div>
                      {/* Content */}
                      <div className="flex-1 pb-5">
                        <Link to={cfg.link} className="block bg-white border border-gray-200 p-4 hover:border-[#002FA7]/30 transition-colors group">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">{cfg.label}</span>
                                {event.status && <Badge className={`rounded-none text-[10px] border capitalize ${statusBadge(event.status)}`}>{event.status}</Badge>}
                              </div>
                              <p className="text-sm font-medium text-[#1A1D20]">{event.title}</p>
                              <p className="text-xs text-[#6B7280] mt-1">{event.subtitle}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-[#6B7280]">{event.date ? event.date.substring(0, 10) : ""}</span>
                              <CaretRight size={14} className="text-gray-300 group-hover:text-[#002FA7] transition-colors" />
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
